/**
 * Shared configuration, geometry, and scroll logic for ParallaxSeats implementations.
 */

/** @typedef {{
 *   containerSelector?: string,
 *   rows?: number,
 *   frontRowWidth?: number,
 *   perspectiveFactor?: number,
 *   parallaxFactor?: [number, number],
 *   fadeOpacityStep?: number,
 * }} ParallaxSeatsOptions */

/** @typedef {{
 *   index: number,
 *   t: number,
 *   seatWidth: number,
 *   rowHeight: number,
 *   zIndex: number,
 *   isFrontRow: boolean,
 *   distFromFront: number,
 *   fadeBottom: string,
 *   fadeTopOpacity: number,
 *   sideOpacity: number,
 *   blur: number,
 *   overlapStart: number | null,
 *   overlapEnd: number | null,
 * }} RowGeometry */

export const DEFAULTS = {
  containerSelector: ".parallax-seats",
  rows: 4,
  frontRowWidth: 235,
  perspectiveFactor: 0.6,
  parallaxFactor: [0.5, 0.8],
  fadeOpacityStep: 0.1,
  responsiveBreakpoint: 768,
  responsiveSizeScale: 0.5,
  parallaxPadding: 30,
};

/**
 * Read theme values from CSS custom properties on the container.
 * @param {Element} container
 */
export function readContainerConfig(container) {
  const styles = getComputedStyle(container);
  return {
    imgW: parseFloat(styles.getPropertyValue("--seat-image-width")) || 470,
    imgH: parseFloat(styles.getPropertyValue("--seat-image-height")) || 400,
    bgBase: styles.getPropertyValue("--seat-bg-base").trim(),
    bgFooter: styles.getPropertyValue("--seat-bg-footer").trim(),
  };
}

/**
 * Calculate geometry for all rows.
 * @param {ParallaxSeatsOptions & { imgW: number, imgH: number, bgBase: string, bgFooter: string }} config
 * @returns {RowGeometry[]}
 */
export function calculateRowGeometries(config) {
  const { rows, frontRowWidth, perspectiveFactor, parallaxFactor, fadeOpacityStep, imgW, imgH, bgBase, bgFooter } =
    config;
  const geometries = [];

  for (let i = 0; i < rows; i++) {
    const t = rows > 1 ? i / (rows - 1) : 1;
    const minWidth = frontRowWidth * (1 - perspectiveFactor);
    const seatWidth = minWidth + (frontRowWidth - minWidth) * t;
    const rowHeight = Math.round(seatWidth * (imgH / imgW));
    const isFrontRow = i === rows - 1;
    const distFromFront = rows - 1 - i;

    geometries.push({
      index: i,
      t,
      seatWidth,
      rowHeight,
      zIndex: i + 1,
      isFrontRow,
      distFromFront,
      fadeBottom: isFrontRow ? bgFooter : bgBase,
      fadeTopOpacity: isFrontRow ? 0 : fadeOpacityStep * distFromFront,
      sideOpacity: Math.min(0.4 + distFromFront * 0.2, 1),
      blur: distFromFront * 0.8,
      overlapStart: isFrontRow ? null : parallaxFactor[0],
      overlapEnd: isFrontRow ? null : parallaxFactor[1],
    });
  }

  return geometries;
}

/**
 * Calculate scroll progress.
 * - Large screens: 0 = container top entering viewport, 1 = page bottom
 * - Small screens: 0 = container top entering viewport, 1 = container top at viewport top
 * @param {Element} container
 * @param {number} breakpoint - viewport width breakpoint in pixels
 * @returns {number}
 */
export function getScrollProgress(container, breakpoint = DEFAULTS.responsiveBreakpoint) {
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const currentScroll = window.scrollY;
  const containerTop = container.offsetTop;
  const containerHeight = container.offsetHeight;
  const enterScroll = Math.max(0, containerTop - viewportHeight);

  if (viewportWidth < breakpoint) {
    // Small screen: collapse when container top reaches viewport top
    const scrollRange = viewportHeight - containerHeight;
    if (scrollRange <= 0) return 1;
    const relativeScroll = currentScroll - Math.max(0, enterScroll);
    return Math.max(0, Math.min(1, relativeScroll / scrollRange));
  } else {
    // Large screen: collapse until page bottom
    const scrollBottom = document.documentElement.scrollHeight - viewportHeight;
    const scrollRange = scrollBottom - Math.max(0, enterScroll);
    return scrollRange > 0 ? Math.max(0, Math.min(1, (currentScroll - Math.max(0, enterScroll)) / scrollRange)) : 1;
  }
}

/**
 * Parse a CSS color to [r, g, b] array (0-255).
 * @param {string} color
 * @returns {[number, number, number]}
 */
export function parseColor(color) {
  const el = document.createElement("div");
  el.style.color = color;
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);
  const match = computed.match(/(\d+),\s*(\d+),\s*(\d+)/);
  return match ? [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])] : [0, 0, 0];
}
